import React, {useEffect, useMemo, useState} from 'react';
import {gql} from '@apollo/client';
import {useLazyQuery, useMutation} from '@apollo/client/react';
import {toast} from 'react-toastify';
import {Box, Checkbox, CircularProgress, FormControlLabel, List, ListItem, Stack, Typography} from '@mui/material';

import {Button} from 'components/forms/Buttons';
import ButtonDialog from 'components/forms/ButtonDialog';
import {useDialogContext} from 'components/dialog/DialogContext';
import {useListContext} from 'components/list/ListContext';

const GET_LIVELOX_PARTICIPANTS = gql`
  query GetLiveloxParticipants($subEventId: Int!) {
    items: getLiveloxParticipants(subEventId: $subEventId) {
      id
      name
      deviceName
      hasRouteData
      alreadyImported
      sessionStart
    }
  }
`;

const IMPORT_LIVELOX_COMPETITORS = gql`
  mutation ImportLiveloxCompetitors($subEventId: Int!, $participantIds: [Int!]) {
    importLiveloxCompetitors(subEventId: $subEventId, participantIds: $participantIds) {
      id
      name
    }
  }
`;

type LiveloxParticipant = {
  id: number;
  name: string;
  deviceName: string;
  hasRouteData: boolean;
  alreadyImported: boolean;
  sessionStart?: string | null;
};

const FORM_ID = 'import_livelox_competitors';

type BodyProps = {
  subEventId: number;
};

const ImportLiveloxCompetitorsBody = ({subEventId}: BodyProps) => {
  const {refetch} = useListContext();
  const {close} = useDialogContext();

  const [fetchParticipants, {data, loading, error}] = useLazyQuery<{items: LiveloxParticipant[]}>(GET_LIVELOX_PARTICIPANTS, {
    fetchPolicy: 'network-only'
  });

  const [selectedIds, setSelectedIds] = useState<Record<number, boolean>>({});

  useEffect(() => {
    fetchParticipants({variables: {subEventId}});
  }, [fetchParticipants, subEventId]);

  const participants = data?.items ?? [];
  const importable = useMemo(() => participants.filter((p) => !p.alreadyImported), [participants]);

  useEffect(() => {
    if (importable.length === 0) return;
    setSelectedIds((prev) => {
      const next: Record<number, boolean> = {};
      for (const p of importable) {
        next[p.id] = prev[p.id] ?? true;
      }
      return next;
    });
  }, [importable]);

  const allSelected = importable.length > 0 && importable.every((p) => selectedIds[p.id]);
  const someSelected = importable.some((p) => selectedIds[p.id]);

  const toggleAll = () => {
    const next: Record<number, boolean> = {};
    const value = !allSelected;
    for (const p of importable) {
      next[p.id] = value;
    }
    setSelectedIds(next);
  };

  const toggleOne = (id: number) => {
    setSelectedIds((prev) => ({...prev, [id]: !prev[id]}));
  };

  const [importCompetitors, {loading: importing}] = useMutation<{importLiveloxCompetitors: {id: number; name: string}[]}>(IMPORT_LIVELOX_COMPETITORS);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const ids = importable.filter((p) => selectedIds[p.id]).map((p) => p.id);
    if (ids.length === 0) {
      toast.error('Select at least one competitor to import');
      return;
    }
    try {
      const resp = await importCompetitors({variables: {subEventId, participantIds: ids}});
      const created = resp.data?.importLiveloxCompetitors ?? [];
      toast.success(`Imported ${created.length} competitor${created.length === 1 ? '' : 's'} from Livelox`);
      if (typeof refetch === 'function') refetch();
      close();
    } catch (e) {
      if (e instanceof Error && e.message) toast.error(e.message);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" py={3}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" variant="body2">
        Failed to load participants: {error.message}
      </Typography>
    );
  }

  if (participants.length === 0) {
    return <Typography variant="body2">No participants found in the Livelox class.</Typography>;
  }

  return (
    <form id={FORM_ID} onSubmit={handleSubmit} noValidate>
      <Stack spacing={1}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <FormControlLabel
            control={
              <Checkbox
                indeterminate={someSelected && !allSelected}
                checked={allSelected}
                onChange={toggleAll}
                disabled={importable.length === 0 || importing}
              />
            }
            label={`Select all (${importable.length})`}
          />
          <Typography variant="caption" color="text.secondary">
            {participants.length - importable.length} already imported
          </Typography>
        </Box>
        <List dense disablePadding>
          {participants.map((p) => {
            const disabled = p.alreadyImported || importing;
            const checked = p.alreadyImported ? true : !!selectedIds[p.id];
            return (
              <ListItem key={p.id} disableGutters>
                <FormControlLabel
                  control={<Checkbox checked={checked} disabled={disabled} onChange={() => toggleOne(p.id)} />}
                  label={
                    <Box>
                      <Typography variant="body2">
                        <strong>{p.name}</strong>{' '}
                        <Typography component="span" variant="caption" color="text.secondary">
                          ({p.deviceName})
                        </Typography>
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {p.alreadyImported ? 'already imported' : p.hasRouteData ? 'has GPS track' : 'no GPS track'}
                        {p.sessionStart && ` · starts ${new Date(p.sessionStart).toLocaleTimeString()}`}
                      </Typography>
                    </Box>
                  }
                />
              </ListItem>
            );
          })}
        </List>
      </Stack>
    </form>
  );
};

type Props = {
  subEventId: number;
};

const ImportLiveloxCompetitorsButton = ({subEventId}: Props) => {
  return (
    <ButtonDialog
      buttonLabel="Import from Livelox"
      dialogTitle="Import competitors from Livelox"
      maxWidth="sm"
      primaryButton={
        <Button type="submit" form={FORM_ID}>
          Import selected
        </Button>
      }
    >
      <ImportLiveloxCompetitorsBody subEventId={subEventId} />
    </ButtonDialog>
  );
};

export default ImportLiveloxCompetitorsButton;
